import React, { FC, useContext, useEffect, useState, useCallback } from 'react';
import { Redirect } from 'react-router-dom';
import createAuth0Client, { Auth0ClientOptions } from '@auth0/auth0-spa-js';
import { env } from '../../../Shared/constants/env';
import GET_CURRENT_USER_QUERY from './getCurrentUserQuery.graphql';
import * as Sentry from '@sentry/browser';
import Auth0Client from '@auth0/auth0-spa-js/dist/typings/Auth0Client';
import { LoadingPage } from '../../../Shared/components/Loading';
import { ApolloClient } from 'apollo-client';
import { buildApolloClient } from '../../../Shared/services/Reznor/gql';
import { ApolloProvider } from '@apollo/react-hooks';
import { NormalizedCacheObject } from 'apollo-cache-inmemory';
import { Switch, Route, useHistory } from 'react-router';
import { Maintenance } from '../Maintenance/Maintenance';
import { Logout } from '../Logout/Logout';
import { GrantsType } from '../../../Shared/utils/auth/types';
import { useAsyncError, useFullstory } from 'Shared/hooks';
import { isMissing } from 'Shared/utils';

export type CurrentUserData = {
  currentUser: {
    id: string;
    activated: boolean;
    firstName: string;
    lastName: string;
    email: string;
    marketingIdentifier: {
      id: string;
    } | null;
    partner: {
      id: string;
      name: string;
    } | null;
    effectiveGrants: GrantsType;
    isCarrierUser: boolean;
    isSystemUser: boolean;
    isInternalUser: boolean;
    preferences: {
      defaultPaymentFrequency: string | null;
    };
  };
};

// https://auth0.github.io/auth0-spa-js/interfaces/auth0clientoptions.html
const auth0Config: Auth0ClientOptions = {
  domain: env.auth0Domain,
  client_id: env.auth0ClientId,
  redirect_uri: env.auth0RedirectUrl,
  audience: env.auth0Audience,
  leeway: 120,
} as const;

const LOGOUT_KEY = 'logout_token';
const LOGOUT_EVENT = 'logout_event';

export type AuthContextProps = {
  isAuthenticated: boolean;
  user: CurrentUserData['currentUser'] | null;
  getToken: () => Promise<string>;
  login: () => void;
  logout: () => void;
};

const noop = () => {};

export const isCarrierUser = (user: CurrentUserData['currentUser'] | null) =>
  user?.partner?.name === 'clearcover';
export const isSystemUser = (user: CurrentUserData['currentUser'] | null) =>
  isMissing(user?.partner);
export const isInternalUser = (user: CurrentUserData['currentUser'] | null) =>
  isCarrierUser(user) || isSystemUser(user);

export const AuthContext = React.createContext<AuthContextProps>({
  isAuthenticated: false,
  user: null,
  getToken: () => Promise.resolve(''),
  login: noop,
  logout: noop,
});

export const useAuth0 = () => useContext(AuthContext);

export { GET_CURRENT_USER_QUERY };

export const AuthProvider: FC<{ children?: React.ReactNode }> = ({
  children,
}) => {
  const throwError = useAsyncError();
  const [auth0Client, setAuth0] = useState<Auth0Client>();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState<CurrentUserData['currentUser'] | null>(null);
  const [client, setClient] = useState<ApolloClient<
    NormalizedCacheObject
  > | null>(null);
  const history = useHistory();

  const { fsCommands } = useFullstory();

  const handleNetworkError = useCallback(
    (err: Error) => {
      if (env.maintenancePageFlag) {
        // Do not show error page; allow for Maintenance routing in the case that reznor or Auth0 is down
        setLoading(false);
        return;
      }
      throwError(err);
    },
    [throwError]
  );

  useEffect(() => {
    const initAuth0 = async () => {
      const auth0FromHook = await createAuth0Client(auth0Config);
      setAuth0(auth0FromHook);

      try {
        if (window.location.search.includes('code=')) {
          const { appState } = await auth0FromHook.handleRedirectCallback();
          history.replace(
            appState && appState.targetUrl
              ? appState.targetUrl
              : window.location.pathname
          );
        }

        const isAuthenticated = await auth0FromHook.isAuthenticated();
        setIsAuthenticated(isAuthenticated);

        if (isAuthenticated) {
          const token = await auth0FromHook.getTokenSilently();
          const client = buildApolloClient(token);
          setClient(client);

          client
            .query<CurrentUserData>({
              query: GET_CURRENT_USER_QUERY,
            })
            .then(({ data }) => {
              const user = data.currentUser;
              if (user) {
                setUser({
                  ...data.currentUser,
                  isCarrierUser: isCarrierUser(data.currentUser),
                  isSystemUser: isSystemUser(data.currentUser),
                  isInternalUser: isInternalUser(data.currentUser),
                });
                fsCommands.updatePersistedFullstoryMetadata({
                  new_marketing_id: user.marketingIdentifier?.id || 'none',
                  new_partner_id: user.partner?.id || 'none',
                  new_partner_name: user.partner?.name || 'none',
                });
                Sentry.configureScope(scope =>
                  scope.setUser({
                    id: data.currentUser.id,
                  })
                );
                if (window.FS) {
                  // Fullstory Custom Data https://app.fullstory.com/ui/J7QZ3/settings/general
                  window.FS.identify(user.id, {
                    displayName: `${user.firstName} ${user.lastName}`,
                    partnerName: user.partner && user.partner.name,
                    partnerId: user.partner && user.partner.id,
                    marketingId:
                      user.marketingIdentifier && user.marketingIdentifier.id,
                  });
                }
              }
              setLoading(false);
            })
            .catch(err => {
              // Handles the case where reznor is down
              handleNetworkError(err);
            });
        } else {
          await auth0FromHook.loginWithRedirect({
            appState: {
              targetUrl: `${window.location.pathname}${window.location.search}`,
            },
          });
        }
      } catch (err) {
        // Handles the case where Auth0 is down
        handleNetworkError(err);
      }
    };
    initAuth0();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [history, handleNetworkError]);

  useEffect(() => {
    const handleMultiTabLogoutEvent = (event: StorageEvent) => {
      if (
        auth0Client &&
        event.key === LOGOUT_KEY &&
        event.newValue === LOGOUT_EVENT
      ) {
        auth0Client.logout();
      }
    };

    localStorage.removeItem(LOGOUT_KEY);
    window.addEventListener('storage', handleMultiTabLogoutEvent);

    return () => {
      localStorage.removeItem(LOGOUT_KEY);
      window.removeEventListener('storage', handleMultiTabLogoutEvent);
    };
  }, [auth0Client]);

  const castLogoutAcrossTabs = () => {
    localStorage.setItem(LOGOUT_KEY, LOGOUT_EVENT);
  };

  const login = () => {
    auth0Client && auth0Client.loginWithRedirect();
  };

  const logout = () => {
    if (auth0Client) {
      auth0Client.logout();
      castLogoutAcrossTabs();
    }
  };

  const getToken = async (): Promise<string> => {
    if (!auth0Client) {
      return Promise.resolve('');
    }
    return auth0Client.getTokenSilently();
  };

  if (loading || (!env.maintenancePageFlag && !isAuthenticated)) {
    return <LoadingPage />;
  }

  return (
    <AuthContext.Provider
      value={{
        user,
        login,
        logout,
        isAuthenticated,
        getToken,
      }}
    >
      <Switch>
        <Route path="/logout" component={Logout} />
        {env.maintenancePageFlag ? (
          <>
            <Route path="/maintenance" component={Maintenance} />
            <Redirect to="/maintenance" />
          </>
        ) : (
          <Route>
            <ApolloProvider client={client!}>{children}</ApolloProvider>
          </Route>
        )}
      </Switch>
    </AuthContext.Provider>
  );
};
